import { nodeSummary, nodeTitle, portLabel } from "./nodeDisplay";
import type { FlowDocument, FlowNode } from "./types";

/**
 * A flow rendered as an outline a person can read before placing a call.
 *
 * The canvas already shows the graph, but a graph is the wrong shape for the
 * question "what will happen when I ring this number" — that question is
 * answered by walking the flow from its start node in execution order, which
 * is what this does. It exists because a tester had no way to see, before
 * dialling, that one branch of a template ended the call without speaking
 * (see flowAdvice.ts for that specific case).
 *
 * Presentation only. Node titles, port labels and config summaries all come
 * from nodeDisplay.ts, so the designer canvas and this outline can never
 * describe the same node differently.
 */
export interface PlanStep {
  /** Nesting depth, 0 for the start node. */
  depth: number;
  /** The node this step is about. */
  nodeId: string;
  /** Human node type, e.g. "Key press menu". */
  title: string;
  /** One-line summary of the node's own config, when it has one. */
  summary: string;
  /**
   * The port taken to ARRIVE here, already labelled ("Key 1", "Live
   * person"). Empty for the start node, which is not arrived at.
   */
  via: string;
  /**
   * Set when this node's onward steps were already shown earlier in the
   * outline, so the walk stops here instead of repeating them. Covers both a
   * real loop and a convergence -- an AMD gate whose "live person" and "not
   * sure" ports both lead to the same menu would otherwise print that whole
   * menu, and everything under it, twice.
   */
  revisited: boolean;
}

function nodesById(document: FlowDocument): Map<string, FlowNode> {
  const map = new Map<string, FlowNode>();
  for (const node of document.nodes) {
    if (node && typeof node.id === "string") map.set(node.id, node);
  }
  return map;
}

/**
 * Outgoing edges grouped by source, each group in the order the ports should
 * be read rather than the order the edges happen to sit in the document.
 *
 * Ports are sorted so the outline reads the way a caller experiences the
 * node: the main path first, then the digit choices in key order, then the
 * things that go wrong. An edge list is otherwise in whatever order the
 * designer wrote it, which makes two functionally identical flows produce
 * two differently ordered outlines.
 */
const PORT_ORDER = ["next", "human", "notsure", "machine", "repeat", "exceeded", "default", "timeout", "invalid", "failure", "error"];

function portRank(port: string): number {
  if (port.startsWith("key:")) return PORT_ORDER.length;
  if (port.startsWith("case:")) return PORT_ORDER.length;
  const index = PORT_ORDER.indexOf(port);
  return index === -1 ? PORT_ORDER.length + 1 : index;
}

function sortedEdges(document: FlowDocument, sourceId: string): FlowDocument["edges"] {
  return document.edges
    .filter((edge) => edge && edge.source === sourceId && typeof edge.target === "string")
    .slice()
    .sort((a, b) => {
      const rank = portRank(a.port) - portRank(b.port);
      return rank !== 0 ? rank : a.port.localeCompare(b.port);
    });
}

/**
 * Walk the flow from its start node into a flat, depth-tagged outline.
 *
 * Flat rather than a tree because every consumer so far wants to render it
 * as an indented list, and a flat list with a depth is trivially renderable
 * while a tree is not trivially flattenable in the same order.
 *
 * A node whose onward steps have already been printed is emitted once more,
 * marked `revisited`, and not expanded again. Tracked globally rather than
 * per path: convergence is normal in these flows, and a per-path rule
 * reprints a whole subtree for every branch that reaches it. Measured on the
 * real `bill-reminder` template, whose AMD gate sends both "live person" and
 * "not sure" to the same menu, that was the difference between a 20-step
 * outline and an 11-step one describing the same call. Cycles fall out of
 * the same rule, so a repeat loop terminates here too.
 *
 * A node with NO onward steps is always printed in full, never marked: there
 * is nothing being elided, and a shared hangup reading "shown above" at the
 * end of every branch would be noise rather than information.
 *
 * Returns an empty outline when the start node is missing rather than
 * throwing; this runs against documents being edited, which are routinely
 * incomplete.
 */
export function flowPlan(document: FlowDocument): PlanStep[] {
  const nodes = nodesById(document);
  const steps: PlanStep[] = [];
  const expanded = new Set<string>();

  const walk = (nodeId: string, via: string, depth: number): void => {
    const node = nodes.get(nodeId);
    if (!node) return;
    const outgoing = sortedEdges(document, nodeId);
    const revisited = outgoing.length > 0 && expanded.has(nodeId);
    steps.push({
      depth,
      nodeId,
      title: nodeTitle(node.type),
      summary: nodeSummary(node),
      via,
      revisited,
    });
    if (revisited) return;
    expanded.add(nodeId);

    // A single onward port is a continuation, not a branch, so it stays at
    // the same depth -- otherwise a long linear flow marches off the right
    // edge of the dialog for no reason. Two or more is a real choice and
    // earns the indent.
    const nextDepth = outgoing.length > 1 ? depth + 1 : depth;
    for (const edge of outgoing) {
      walk(edge.target, portLabel(edge.port), nextDepth);
    }
  };

  walk(document.start_node_id, "", 0);
  return steps;
}

/**
 * Narrow an API-supplied `program` (typed `unknown`, because the server
 * owns the schema and the client does not re-declare it) to the minimal
 * document shape the outline and the advice need.
 *
 * Returns null rather than throwing, and rather than the bare cast used
 * where a whole designer screen is being loaded: this runs inside the
 * call-placing form, where a program that fails to parse must cost the user
 * a disabled button and nothing more. Only the fields actually read are
 * checked; a node's `config` stays unknown exactly as types.ts intends.
 */
export function asFlowDocument(program: unknown): FlowDocument | null {
  if (!program || typeof program !== "object") return null;
  const raw = program as Record<string, unknown>;
  if (typeof raw.start_node_id !== "string") return null;
  if (!Array.isArray(raw.nodes) || !Array.isArray(raw.edges)) return null;

  const nodes = raw.nodes.filter(
    (node): node is FlowNode =>
      !!node && typeof node === "object" && typeof (node as FlowNode).id === "string" && typeof (node as FlowNode).type === "string",
  );
  const edges = raw.edges.filter(
    (edge): edge is FlowDocument["edges"][number] =>
      !!edge &&
      typeof edge === "object" &&
      typeof (edge as FlowDocument["edges"][number]).source === "string" &&
      typeof (edge as FlowDocument["edges"][number]).port === "string" &&
      typeof (edge as FlowDocument["edges"][number]).target === "string",
  );

  return {
    schema_version: typeof raw.schema_version === "string" ? raw.schema_version : "",
    id: typeof raw.id === "string" ? raw.id : "",
    direction: raw.direction === "inbound" ? "inbound" : "outbound",
    start_node_id: raw.start_node_id,
    variables: Array.isArray(raw.variables) ? raw.variables : [],
    nodes,
    edges,
  };
}
